import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Container,
  DataGridButton,
  Title,
  UserDeleteOptionsGreen,
  UserDeleteOptionsRed,
  ReviewModalContainer,
  ReviewSection,
  ReviewLabel,
  ReviewSelect,
  ReviewButton,
} from "./styles";
import { Alert, AlertColor, Avatar, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { UserPermissions } from "../../App";
import Status from "../../Components/Status";
import ReusableModal from "../../Components/Modal";

interface Props {
  userPermissions: UserPermissions;
}

const extensionStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Deprecated", value: "deprecated" },
  { label: "Revoked", value: "revoked" },
];

const Extensions: React.FC<Props> = ({ userPermissions }) => {
  const navigate = useNavigate();

  const [packageID, setPackageID] = useState("");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedExtensionId, setSelectedExtensionId] = useState<string>("");
  const [reviewStatus, setReviewStatus] = useState<string>("");

  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 300,
      renderCell: (params: any) => (
        <>
          <Avatar
            src={params.row.logo}
            sx={{ height: "2rem", width: "2rem", marginRight: "1rem" }}
          />{" "}
          {params.value}
        </>
      ),
    },
    {
      field: "id",
      headerName: "Package ID",
      width: 240,
    },
    {
      field: "publisher",
      headerName: "Publisher",
      width: 200,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params: any) => (
        <Status status={params.value} noBorder={true} />
      ),
    },
    /* {
      field: "latestVersion",
      headerName: "Latest Version",
      width: 150,
    }, */
    {
      field: "action",
      headerName: "Action",
      width: 380,
      renderCell: (params: any) => (
        <span style={{}}>
          <DataGridButton
            onClick={() => {
              navigate(
                process.env.PUBLIC_URL + "/Extensions/View/" + params.row.id
              );
            }}
          >
            View
          </DataGridButton>
          <DataGridButton
            color="#ff9a02"
            onClick={() => {
              setSelectedExtensionId(params.row.id);
              setReviewStatus(params.row.status || "active");
              setIsReviewModalOpen(true);
            }}
          >
            Review
          </DataGridButton>
          {packageID === params.row.id ? (
            <>
              Delete?{" "}
              <UserDeleteOptionsRed
                onClick={() => {
                  deleteExtension(params.row.id);
                  setPackageID("");
                }}
              >
                Yes
              </UserDeleteOptionsRed>{" "}
              <UserDeleteOptionsGreen
                onClick={() => {
                  setPackageID("");
                }}
              >
                No
              </UserDeleteOptionsGreen>
            </>
          ) : (
            params.row.canDelete && (
              <DataGridButton
                style={{ color: "#DB3A34", border: "2px solid #DB3A34" }}
                onClick={() => {
                  setPackageID(params.row.id);
                }}
              >
                Delete
              </DataGridButton>
            )
          )}
        </span>
      ),
    },
  ];

  const [tableData, setTableData] = useState([]);

  const deleteExtension = (id: string) => {
    console.log("Delete: ", id);
    fetch("/api/v1/extension/remove", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.success === true) {
          getExtensions();
          setStatusMsg("Extension deleted!");
          setStatusSeverity("success");
          setShowStatus(true);
        } else {
          getExtensions();
          setStatusMsg(`Cannot delete extension: ${data.reason}`);
          setStatusSeverity("error");
          setShowStatus(true);
        }
      }).catch((err) => {
        console.log("Cannot delete extension:", err);
        getExtensions();
        setStatusMsg(`Cannot delete extension: Unknown Error`);
        setStatusSeverity("error");
        setShowStatus(true);
      })
  };

  const changeExtensionStatus = (id: string, status: string) => {
    console.log(`Changing status of extension ${id} to ${status}`);
    fetch("/api/v1/extension/changeStatus", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id,
        status,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.success === true) {
          getExtensions();
          setStatusMsg(`Extension status changed to ${status}!`);
          setStatusSeverity("success");
          setShowStatus(true);
          setIsReviewModalOpen(false);
        } else {
          setStatusMsg(`Cannot change status: ${data.reason}`);
          setStatusSeverity("error");
          setShowStatus(true);
        }
      }).catch((err) => {
        console.log("Cannot change extension status:", err);
        setStatusMsg(`Cannot change status: Unknown Error`);
        setStatusSeverity("error");
        setShowStatus(true);
      })
  };

  const getExtensions = () => {
    fetch("/api/v1/extension/list", {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === true) {
          setTableData(
            data.extensionList.map((val: any) => {
              return {
                id: val.id,
                name: val.name,
                logo: val.logo,
                publisher: val.publisher.id,
                latestVersion: val.latestVersion || "N/A",
                status: val.status,
                canDelete: true,
              };
            })
          );
          setTableLoading(false);
        }
      }).catch((err) => console.log("Error while fetching extension list:", err));
  };

  const [tableLoading, setTableLoading] = useState(false);
  useEffect(() => {
    setTableLoading(true);
    getExtensions();

    const interval = setInterval(() => {
      getExtensions();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const [showStatus, setShowStatus] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState("");
  const [statusSeverity, setStatusSeverity] =
    React.useState<AlertColor>("error");
  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowStatus(false);
  };

  return (
    <Container>
      <Title>
        Extensions{" "}
        <Button
          onClick={() => {
            navigate(process.env.PUBLIC_URL + "/Extensions/Create");
          }}
        >
          Add new extension
        </Button>
      </Title>

      <div style={{ height: 500, width: "100%", paddingTop: "1rem" }}>
        <DataGrid
          style={{ fontWeight: 900, color: "rgba(55, 73, 87, 0.7)" }}
          rows={tableData}
          columns={columns}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          loading={tableLoading}
        />
      </div>

      <ReusableModal open={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)}>
        <ReviewModalContainer>
          <h2>Change Extension Status</h2>

          <ReviewSection>
            <ReviewLabel>New Status</ReviewLabel>
            <ReviewSelect
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
            >
              {extensionStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ReviewSelect>
          </ReviewSection>

          <ReviewSection className="buttons">
            <ReviewButton
              cancel
              onClick={() => setIsReviewModalOpen(false)}
            >
              Cancel
            </ReviewButton>
            <ReviewButton
              onClick={() => {
                changeExtensionStatus(selectedExtensionId, reviewStatus);
              }}
            >
              Change Status
            </ReviewButton>
          </ReviewSection>
        </ReviewModalContainer>
      </ReusableModal>

      <Snackbar
        open={showStatus}
        autoHideDuration={2000}
        onClose={handleCloseStatus}
      >
        <Alert
          onClose={handleCloseStatus}
          severity={statusSeverity}
          sx={{ width: "100%" }}
        >
          {statusMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Extensions;
